import React, { useState, useEffect, useCallback } from 'react';
import { useLocalization } from '../../hooks/useLocalization';
import HotelDetailEditor from './modules/hotels/HotelDetailEditor';
import { getThumbnailUrl } from '../../utils/imageUtils';

interface HotelListItem {
    id: string;
    tourvisioId: string;
    name: string;
    stars: number;
    location: {
        city: string;
        country: string;
        address?: string;
    };
    shortDescription?: string;
    thumbnail: string;
    imagesCount: number;
    facilitiesCount: number;
    roomTypesCount: number;
    boardTypesCount: number;
    isActive: boolean;
    isFeatured: boolean;
    isSiteFeatured?: boolean;
    isCallToAction?: boolean;
    showInHomepage?: boolean;
    showInSearch?: boolean;
    lockContent: boolean;
    lastSynced: string;
    dataSource?: 'local' | 'api' | 'synced';
    productType?: number | null;
}

interface HotelStats {
    total: number;
    active: number;
    featured: number;
    withImages: number;
    withRooms: number;
    citiesCount: number;
    countriesCount: number;
}

const API_BASE = '/api/hotels';

interface HotelManagerProps {
    theme?: 'light' | 'dark';
}

const HotelManager: React.FC<HotelManagerProps> = ({ theme = 'dark' }) => {
    const isDark = theme === 'dark';
    const { t, language } = useLocalization();
    const [hotels, setHotels] = useState<HotelListItem[]>([]);
    const [stats, setStats] = useState<HotelStats | null>(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    // View mode
    const [selectedHotelId, setSelectedHotelId] = useState<string | null>(null);
    const [viewMode, setViewMode] = useState<'list' | 'detail'>('list');

    // Filters
    const [search, setSearch] = useState('');
    const [starFilter, setStarFilter] = useState<number | null>(null);
    const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({ total: 0, pages: 0 });
    const [isSyncing, setIsSyncing] = useState(false);
    const [isGeneratingAI, setIsGeneratingAI] = useState(false);
    const [productTypeFilter, setProductTypeFilter] = useState<number | null>(null);

    const showMessage = (type: 'success' | 'error', text: string) => {
        setMessage({ type, text });
        setTimeout(() => setMessage(null), 3000);
    };

    const fetchHotels = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                limit: '20',
                search
            });
            if (starFilter) params.set('minStars', starFilter.toString());
            if (statusFilter !== 'all') params.set('active', (statusFilter === 'active').toString());
            if (productTypeFilter) params.set('productType', productTypeFilter.toString());

            const res = await fetch(`${API_BASE}?${params}`);
            const data = await res.json();
            if (data.success) {
                setHotels(data.hotels);
                setPagination({ total: data.pagination.total, pages: data.pagination.pages });
            }
        } catch (err) {
            console.error('Failed to fetch hotels:', err);
        }
        setLoading(false);
    }, [page, search, starFilter, statusFilter, productTypeFilter]);

    const fetchStats = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE}/stats/summary`);
            const data = await res.json();
            if (data.success) setStats(data.stats);
        } catch (err) {
            console.error('Failed to fetch stats:', err);
        }
    }, []);

    useEffect(() => {
        fetchHotels();
        fetchStats();
    }, [fetchHotels, fetchStats]);

    useEffect(() => {
        const delaySearch = setTimeout(() => {
            setPage(1);
            fetchHotels();
        }, 300);
        return () => clearTimeout(delaySearch);
    }, [search]);

    const openHotelDetail = (hotelId: string) => {
        setSelectedHotelId(hotelId);
        setViewMode('detail');
    };

    const backToList = () => {
        setSelectedHotelId(null);
        setViewMode('list');
        fetchHotels();
        fetchStats();
    };

    // Sync hotels from Tourvisio API
    const syncFromAPI = async () => {
        setIsSyncing(true);
        showMessage('success', language === 'sv' ? 'Synkroniserar hotell från API...' : 'Syncing hotels from API...');

        try {
            const response = await fetch(`${API_BASE}/sync-from-tourvisio`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    limit: 100
                })
            });

            const data = await response.json();

            if (data.success) {
                showMessage('success',
                    language === 'sv'
                        ? `Synkronisering klar! ${data.totalNew} nya, ${data.totalUpdated} uppdaterade`
                        : `Sync complete! ${data.totalNew} new, ${data.totalUpdated} updated`
                );
                // Refresh the hotels list
                fetchHotels();
                fetchStats();
            } else {
                showMessage('error', data.error || (language === 'sv' ? 'Synkronisering misslyckades' : 'Sync failed'));
            }
        } catch (error) {
            console.error('Sync error:', error);
            showMessage('error', language === 'sv' ? 'Synkronisering misslyckades' : 'Sync failed');
        }

        setIsSyncing(false);
    };

    // Generate AI content for hotels
    const generateAIContent = async () => {
        setIsGeneratingAI(true);
        showMessage('success', language === 'sv' ? 'Genererar AI-innehåll...' : 'Generating AI content...');

        // Simulate AI generation
        await new Promise(resolve => setTimeout(resolve, 2500));

        showMessage('success', language === 'sv' ? 'AI-innehåll genererat!' : 'AI content generated!');
        setIsGeneratingAI(false);
    };

    // Quick toggle hotel property
    const toggleHotelProperty = async (hotelId: string, property: string, currentValue: boolean, e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent opening detail page
        try {
            const res = await fetch(`${API_BASE}/${hotelId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ [property]: !currentValue })
            });
            const data = await res.json();
            if (data.success !== false) {
                // Update local state
                setHotels(prev => prev.map(h =>
                    h.id === hotelId ? { ...h, [property]: !currentValue } : h
                ));
                showMessage('success', language === 'sv' ? 'Uppdaterad!' : 'Updated!');
            }
        } catch (error) {
            showMessage('error', language === 'sv' ? 'Kunde inte uppdatera' : 'Failed to update');
        }
    };

    // If viewing detail, show the detail page
    if (viewMode === 'detail' && selectedHotelId) {
        return <HotelDetailEditor hotelId={selectedHotelId} onClose={backToList} />;
    }



    return (
        <div className={isDark ? 'text-white' : 'text-gray-900'}>
            {/* Message Toast */}
            {message && (
                <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg text-white ${message.type === 'success' ? 'bg-green-600' : 'bg-red-600'
                    }`}>
                    {message.text}
                </div>
            )}

            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-xl md:text-2xl font-bold">{t('hotel_manager')}</h1>
                    <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {language === 'sv' ? 'Hantera alla hotell i systemet' : 'Manage all hotels in the system'}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2 md:gap-3">
                    <button
                        onClick={syncFromAPI}
                        disabled={isSyncing}
                        className="px-3 py-2 text-sm md:text-base md:px-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all flex items-center gap-2 disabled:opacity-50"
                    >
                        <span className={`material-icons text-base md:text-lg ${isSyncing ? 'animate-spin' : ''}`}>
                            {isSyncing ? 'sync' : 'cloud_sync'}
                        </span>
                        <span className="hidden sm:inline">
                            {isSyncing
                                ? (language === 'sv' ? 'Synkar...' : 'Syncing...')
                                : (language === 'sv' ? 'Synka från API' : 'Sync from API')}
                        </span>
                        <span className="sm:hidden">{isSyncing ? '...' : 'Sync'}</span>
                    </button>
                    <button
                        onClick={generateAIContent}
                        disabled={isGeneratingAI}
                        className="px-3 py-2 text-sm md:text-base md:px-4 bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-lg hover:from-teal-700 hover:to-cyan-700 transition-all flex items-center gap-2 disabled:opacity-50"
                    >
                        <span className={`material-icons text-base md:text-lg ${isGeneratingAI ? 'animate-pulse' : ''}`}>auto_awesome</span>
                        <span className="hidden sm:inline">
                            {isGeneratingAI
                                ? (language === 'sv' ? 'Genererar...' : 'Generating...')
                                : (language === 'sv' ? 'AI Innehåll' : 'AI Content')}
                        </span>
                        <span className="sm:hidden">AI</span>
                    </button>
                    <button
                        onClick={() => fetchHotels()}
                        className={`px-3 py-2 text-sm md:text-base md:px-4 rounded-lg transition-colors flex items-center gap-2 ${isDark ? 'bg-slate-700 text-white hover:bg-slate-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                    >
                        <span className="material-icons text-base md:text-lg">refresh</span>
                        <span className="hidden sm:inline">{t('refresh')}</span>
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            {stats && (
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
                    <div className={`rounded-xl p-4 border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
                        <div className={`text-2xl font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{stats.total}</div>
                        <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{language === 'sv' ? 'Totalt' : 'Total'}</div>
                    </div>
                    <div className={`rounded-xl p-4 border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
                        <div className={`text-2xl font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>{stats.active}</div>
                        <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{language === 'sv' ? 'Aktiva' : 'Active'}</div>
                    </div>
                    <div className={`rounded-xl p-4 border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
                        <div className={`text-2xl font-bold ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>{stats.featured}</div>
                        <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{language === 'sv' ? 'Utvalda' : 'Featured'}</div>
                    </div>
                    <div className={`rounded-xl p-4 border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
                        <div className={`text-2xl font-bold ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>{stats.withImages}</div>
                        <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{language === 'sv' ? 'Med bilder' : 'With Images'}</div>
                    </div>
                    <div className={`rounded-xl p-4 border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
                        <div className={`text-2xl font-bold ${isDark ? 'text-pink-400' : 'text-pink-600'}`}>{stats.withRooms}</div>
                        <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{language === 'sv' ? 'Med rum' : 'With Rooms'}</div>
                    </div>
                    <div className={`rounded-xl p-4 border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
                        <div className={`text-2xl font-bold ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`}>{stats.citiesCount}</div>
                        <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{language === 'sv' ? 'Städer' : 'Cities'}</div>
                    </div>
                    <div className={`rounded-xl p-4 border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
                        <div className={`text-2xl font-bold ${isDark ? 'text-teal-400' : 'text-teal-600'}`}>{stats.countriesCount}</div>
                        <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{language === 'sv' ? 'Länder' : 'Countries'}</div>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className={`rounded-xl p-4 mb-6 border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
                <div className="flex flex-wrap gap-4 items-center">
                    {/* Search */}
                    <div className="flex-1 min-w-[200px]">
                        <div className="relative">
                            <svg className={`w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={language === 'sv' ? 'Sök hotell...' : 'Search hotels...'}
                                className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${isDark ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-400' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500'}`}
                            />
                        </div>
                    </div>

                    {/* Star Filter */}
                    <div>
                        <select
                            value={starFilter || ''}
                            onChange={(e) => setStarFilter(e.target.value ? parseInt(e.target.value) : null)}
                            className={`px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-teal-500 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                        >
                            <option value="">{language === 'sv' ? 'Alla stjärnor' : 'All Stars'}</option>
                            {[5, 4, 3, 2, 1].map(star => (
                                <option key={star} value={star}>{'⭐'.repeat(star)}</option>
                            ))}
                        </select>
                    </div>

                    {/* Product Type Filter */}
                    <div>
                        <select
                            value={productTypeFilter || ''}
                            onChange={(e) => setProductTypeFilter(e.target.value ? parseInt(e.target.value) : null)}
                            className={`px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-teal-500 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                        >
                            <option value="">{language === 'sv' ? 'Alla typer' : 'All Types'}</option>
                            <option value="2">🏨 {language === 'sv' ? 'Bara Hotell' : 'Only Hotel'}</option>
                            <option value="1">✈️ {language === 'sv' ? 'Paket' : 'Package'}</option>
                        </select>
                    </div>

                    {/* Status Filter */}
                    <div className={`flex rounded-lg p-1 ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>
                        {(['all', 'active', 'inactive'] as const).map((status) => (
                            <button
                                key={status}
                                onClick={() => setStatusFilter(status)}
                                className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${statusFilter === status
                                    ? 'bg-teal-600 text-white'
                                    : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'
                                    }`}
                            >
                                {status === 'all'
                                    ? (language === 'sv' ? 'Alla' : 'All')
                                    : status === 'active'
                                        ? (language === 'sv' ? 'Aktiva' : 'Active')
                                        : (language === 'sv' ? 'Inaktiva' : 'Inactive')}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Hotels Grid */}
            <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
                {loading && hotels.length === 0 ? (
                    <div className="flex items-center justify-center py-16">
                        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-teal-500"></div>
                    </div>
                ) : hotels.length === 0 ? (
                    <div className={`text-center py-16 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <p>{t('no_data')}</p>
                    </div>
                ) : (
                    <div className={`divide-y ${isDark ? 'divide-slate-700' : 'divide-gray-200'}`}>
                        {hotels.map(hotel => (
                            <div
                                key={hotel.id}
                                onClick={() => openHotelDetail(hotel.id)}
                                className={`p-4 cursor-pointer transition-colors group ${isDark ? 'hover:bg-slate-700/50' : 'hover:bg-gray-50'}`}
                            >
                                <div className="flex gap-4">
                                    {/* Thumbnail */}
                                    <div className="flex-shrink-0 relative">
                                        {hotel.thumbnail ? (
                                            <img
                                                src={getThumbnailUrl(hotel.thumbnail, 200) || ''}
                                                alt=""
                                                className={`w-24 h-24 rounded-lg object-cover ${isDark ? 'bg-slate-800' : 'bg-gray-100'}`}
                                            />
                                        ) : (
                                            <div className={`w-24 h-24 rounded-lg flex items-center justify-center text-3xl ${isDark ? 'bg-slate-600' : 'bg-gray-200'}`}>
                                                🏨
                                            </div>
                                        )}
                                        <div className="absolute top-1 left-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded backdrop-blur-sm">
                                            ID: {hotel.tourvisioId}
                                        </div>
                                    </div>

                                    {/* Info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-start mb-1">
                                            <div>
                                                <h3 className={`font-bold text-lg transition-colors flex items-center gap-2 ${isDark ? 'group-hover:text-teal-400' : 'group-hover:text-teal-600'}`}>
                                                    {hotel.name}
                                                    {hotel.lockContent && (
                                                        <span className="text-red-400 text-xs border border-red-500/30 bg-red-500/10 px-1.5 py-0.5 rounded flex items-center gap-1" title="Content Locked (Manual Override)">
                                                            <span className="material-icons text-[10px]">lock</span>
                                                            Locked
                                                        </span>
                                                    )}
                                                </h3>
                                                <div className={`flex items-center gap-4 text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                                    <span className="text-yellow-400 font-medium">{'★'.repeat(hotel.stars || 0)}</span>
                                                    <span className="flex items-center gap-1">
                                                        <span className="material-icons text-sm">location_on</span>
                                                        {hotel.location?.city}, {hotel.location?.country}
                                                    </span>
                                                    <span className={`text-xs px-1.5 py-0.5 rounded flex items-center gap-1 ${isDark ? 'bg-slate-600/50' : 'bg-gray-200'}`} title="Internal ID">
                                                        <span className="material-icons text-[10px]">fingerprint</span>
                                                        {hotel.id.substring(hotel.id.length - 6)}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Data Source Badge */}
                                            <div>
                                                {hotel.dataSource === 'synced' && (
                                                    <span className={`px-2 py-1 rounded text-xs flex items-center gap-1 border ${isDark ? 'bg-purple-600/20 text-purple-300 border-purple-500/30' : 'bg-purple-100 text-purple-700 border-purple-300'}`}>
                                                        <span className="material-icons text-xs">cloud_done</span>
                                                        {language === 'sv' ? 'Synkroniserad' : 'Synced'}
                                                    </span>
                                                )}
                                                {hotel.dataSource === 'api' && (
                                                    <span className={`px-2 py-1 rounded text-xs flex items-center gap-1 border ${isDark ? 'bg-blue-600/20 text-blue-300 border-blue-500/30' : 'bg-blue-100 text-blue-700 border-blue-300'}`}>
                                                        <span className="material-icons text-xs">api</span>
                                                        API Only
                                                    </span>
                                                )}
                                                {hotel.productType === 2 && (
                                                    <span className={`px-2 py-1 rounded text-xs flex items-center gap-1 border ${isDark ? 'bg-emerald-600/20 text-emerald-300 border-emerald-500/30' : 'bg-emerald-100 text-emerald-700 border-emerald-300'}`}>
                                                        <span className="material-icons text-xs">hotel</span>
                                                        {language === 'sv' ? 'Bara Hotell' : 'Only Hotel'}
                                                    </span>
                                                )}
                                                {hotel.productType === 1 && (
                                                    <span className={`px-2 py-1 rounded text-xs flex items-center gap-1 border ${isDark ? 'bg-sky-600/20 text-sky-300 border-sky-500/30' : 'bg-sky-100 text-sky-700 border-sky-300'}`}>
                                                        <span className="material-icons text-xs">flight</span>
                                                        {language === 'sv' ? 'Paket' : 'Package'}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Short Description */}
                                        <p className={`text-sm mb-3 line-clamp-2 pr-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                            {hotel.shortDescription || (language === 'sv' ? 'Ingen beskrivning tillgänglig.' : 'No description available.')}
                                        </p>

                                        {/* Quick Toggles */}
                                        <div className="flex flex-wrap gap-2">
                                            {/* Active Toggle */}
                                            <button
                                                onClick={(e) => toggleHotelProperty(hotel.id, 'isActive', hotel.isActive, e)}
                                                className={`px-2 py-1 rounded text-xs border flex items-center gap-1 transition-colors ${hotel.isActive
                                                    ? 'bg-green-600/20 border-green-600/50 text-green-400 hover:bg-green-600/30'
                                                    : isDark ? 'bg-slate-700 border-slate-600 text-gray-400 hover:bg-slate-600' : 'bg-gray-100 border-gray-300 text-gray-500 hover:bg-gray-200'
                                                    }`}
                                                title="Toggle Active Status"
                                            >
                                                <span className="material-icons text-xs">{hotel.isActive ? 'check_circle' : 'radio_button_unchecked'}</span>
                                                {language === 'sv' ? 'Aktiv' : 'Active'}
                                            </button>

                                            {/* Featured Toggle */}
                                            <button
                                                onClick={(e) => toggleHotelProperty(hotel.id, 'isFeatured', hotel.isFeatured, e)}
                                                className={`px-2 py-1 rounded text-xs border flex items-center gap-1 transition-colors ${hotel.isFeatured
                                                    ? 'bg-amber-600/20 border-amber-600/50 text-amber-400 hover:bg-amber-600/30'
                                                    : isDark ? 'bg-slate-700 border-slate-600 text-gray-400 hover:bg-slate-600' : 'bg-gray-100 border-gray-300 text-gray-500 hover:bg-gray-200'
                                                    }`}
                                                title="Toggle Featured Status"
                                            >
                                                <span className="material-icons text-xs">{hotel.isFeatured ? 'star' : 'star_border'}</span>
                                                {language === 'sv' ? 'Utvald' : 'Featured'}
                                            </button>

                                            {/* Site Featured Toggle */}
                                            <button
                                                onClick={(e) => toggleHotelProperty(hotel.id, 'isSiteFeatured', !!hotel.isSiteFeatured, e)}
                                                className={`px-2 py-1 rounded text-xs border flex items-center gap-1 transition-colors ${hotel.isSiteFeatured
                                                    ? 'bg-pink-600/20 border-pink-600/50 text-pink-400 hover:bg-pink-600/30'
                                                    : isDark ? 'bg-slate-700 border-slate-600 text-gray-400 hover:bg-slate-600' : 'bg-gray-100 border-gray-300 text-gray-500 hover:bg-gray-200'
                                                    }`}
                                                title="Show in Homepage Hero / Site Featured"
                                            >
                                                <span className="material-icons text-xs">{hotel.isSiteFeatured ? 'favorite' : 'favorite_border'}</span>
                                                {language === 'sv' ? 'Site Featured' : 'Site Featured'}
                                            </button>

                                            {/* Call to Action Toggle */}
                                            <button
                                                onClick={(e) => toggleHotelProperty(hotel.id, 'isCallToAction', !!hotel.isCallToAction, e)}
                                                className={`px-2 py-1 rounded text-xs border flex items-center gap-1 transition-colors ${hotel.isCallToAction
                                                    ? 'bg-purple-600/20 border-purple-600/50 text-purple-400 hover:bg-purple-600/30'
                                                    : isDark ? 'bg-slate-700 border-slate-600 text-gray-400 hover:bg-slate-600' : 'bg-gray-100 border-gray-300 text-gray-500 hover:bg-gray-200'
                                                    }`}
                                                title="Include in Call to Action"
                                            >
                                                <span className="material-icons text-xs">{hotel.isCallToAction ? 'campaign' : 'campaign'}</span>
                                                CTA
                                            </button>

                                            {/* Homepage List Toggle */}
                                            <button
                                                onClick={(e) => toggleHotelProperty(hotel.id, 'showInHomepage', !!hotel.showInHomepage, e)}
                                                className={`px-2 py-1 rounded text-xs border flex items-center gap-1 transition-colors ${hotel.showInHomepage
                                                    ? 'bg-blue-600/20 border-blue-600/50 text-blue-400 hover:bg-blue-600/30'
                                                    : isDark ? 'bg-slate-700 border-slate-600 text-gray-400 hover:bg-slate-600' : 'bg-gray-100 border-gray-300 text-gray-500 hover:bg-gray-200'
                                                    }`}
                                                title="Show in Homepage List"
                                            >
                                                <span className="material-icons text-xs">{hotel.showInHomepage ? 'home' : 'home'}</span>
                                                Home
                                            </button>

                                            {/* Search Toggle */}
                                            <button
                                                onClick={(e) => toggleHotelProperty(hotel.id, 'showInSearch', hotel.showInSearch ?? true, e)}
                                                className={`px-2 py-1 rounded text-xs border flex items-center gap-1 transition-colors ${hotel.showInSearch !== false
                                                    ? 'bg-teal-600/20 border-teal-600/50 text-teal-400 hover:bg-teal-600/30'
                                                    : isDark ? 'bg-slate-700 border-slate-600 text-gray-400 hover:bg-slate-600' : 'bg-gray-100 border-gray-300 text-gray-500 hover:bg-gray-200'
                                                    }`}
                                                title="Include in Search Results"
                                            >
                                                <span className="material-icons text-xs">{hotel.showInSearch !== false ? 'search' : 'search_off'}</span>
                                                Search
                                            </button>
                                        </div>
                                    </div>

                                    {/* Stats (Hidden on small screens) */}
                                    <div className={`hidden lg:flex flex-col gap-2 justify-center border-l pl-4 w-32 ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                                        <div className={`flex justify-between text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                            <span>{language === 'sv' ? 'Bilder' : 'Images'}</span>
                                            <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{hotel.imagesCount}</span>
                                        </div>
                                        <div className={`flex justify-between text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                            <span>{language === 'sv' ? 'Rum' : 'Rooms'}</span>
                                            <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{hotel.roomTypesCount}</span>
                                        </div>
                                        <div className={`flex justify-between text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                            <span>{language === 'sv' ? 'Faciliteter' : 'Facilities'}</span>
                                            <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{hotel.facilitiesCount}</span>
                                        </div>
                                    </div>

                                    {/* Arrow */}
                                    <div className="flex items-center pl-2">
                                        <svg className={`w-5 h-5 text-gray-500 transition-colors ${isDark ? 'group-hover:text-white' : 'group-hover:text-gray-900'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Pagination */}
                {pagination.pages > 1 && (
                    <div className={`p-4 border-t flex flex-wrap items-center justify-between gap-3 ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                        <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {language === 'sv'
                                ? `Visar sida ${page} av ${pagination.pages} (${pagination.total} hotell)`
                                : `Showing page ${page} of ${pagination.pages} (${pagination.total} hotels)`}
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page <= 1}
                                className={`px-3 py-2 text-sm md:px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${isDark ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                            >
                                ← <span className="hidden sm:inline">{language === 'sv' ? 'Föregående' : 'Previous'}</span>
                            </button>
                            <button
                                onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                                disabled={page >= pagination.pages}
                                className={`px-3 py-2 text-sm md:px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${isDark ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                            >
                                <span className="hidden sm:inline">{language === 'sv' ? 'Nästa' : 'Next'}</span> →
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default HotelManager;
